/**
 * Security utilities for input validation and sanitization
 */

import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true // Keep text content but remove all tags
  });
}

/**
 * Sanitize plain text by removing potentially dangerous characters
 */
export function sanitizeText(text: string): string {
  // Remove HTML tags
  let sanitized = text.replace(/<[^>]*>/g, '');
  
  // Remove javascript: and data: protocols
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');
  
  // Remove event handlers
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  // Remove script tags and content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  return sanitized.trim();
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 255;
}

/**
 * Validate name (alphanumeric, spaces, hyphens, apostrophes)
 */
export function isValidName(name: string): boolean {
  // Allow letters, spaces, hyphens, apostrophes, and common accents
  const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]+$/;
  return nameRegex.test(name) && name.length >= 1 && name.length <= 100;
}

/**
 * Validate message content
 */
export function isValidMessage(message: string): boolean {
  // Check length
  if (message.length < 10 || message.length > 5000) {
    return false;
  }
  
  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/gi,
    /javascript:/gi,
    /onerror=/gi,
    /onload=/gi,
    /onclick=/gi,
    /onmouseover=/gi,
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /data:text\/html/gi
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(message));
}

/**
 * Validate and sanitize form input
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  sanitized: {
    name: string;
    email: string;
    message: string;
  };
}

export function validateContactForm(data: {
  name: string;
  email: string;
  message: string;
}): ValidationResult {
  const errors: Record<string, string> = {};
  
  // Sanitize inputs
  const sanitized = {
    name: sanitizeText(data.name.trim()),
    email: data.email.trim().toLowerCase(),
    message: sanitizeText(data.message.trim())
  };
  
  // Validate name
  if (!sanitized.name) {
    errors.name = 'Le nom est requis';
  } else if (!isValidName(sanitized.name)) {
    errors.name = 'Le nom contient des caractères non autorisés';
  } else if (sanitized.name.length > 100) {
    errors.name = 'Le nom ne peut pas dépasser 100 caractères';
  }
  
  // Validate email
  if (!sanitized.email) {
    errors.email = 'L\'email est requis';
  } else if (!isValidEmail(sanitized.email)) {
    errors.email = 'Format d\'email invalide';
  }
  
  // Validate message
  if (!sanitized.message) {
    errors.message = 'Le message est requis';
  } else if (!isValidMessage(sanitized.message)) {
    if (sanitized.message.length < 10) {
      errors.message = 'Le message doit contenir au moins 10 caractères';
    } else if (sanitized.message.length > 5000) {
      errors.message = 'Le message ne peut pas dépasser 5000 caractères';
    } else {
      errors.message = 'Le message contient du contenu non autorisé';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitized
  };
}

/**
 * Get client IP address from request
 */
export function getClientIp(request: Request): string {
  // Try various headers that might contain the real IP
  const forwarded = request.headers.get('x-forwarded-for');
  if (forwarded) {
    return forwarded.split(',')[0].trim();
  }
  
  const realIp = request.headers.get('x-real-ip');
  if (realIp) {
    return realIp;
  }
  
  // Fallback to connection remote address if available
  return 'unknown';
}



